import api from "./api";
import {
  CAPTACION_UPLOAD_MAX_RETRIES,
  CAPTACION_UPLOAD_PARALLEL,
  CAPTACION_UPLOAD_RETRY_MS,
  sleep,
  uploadCaptacionDocumento,
  uploadCaptacionFoto,
  uploadCaptacionVideo,
} from "./captacionUpload";
import { humanizarErrorSubida } from "../utils/humanizarErrorSubida";
import { mostrarAvisoVitalCasa } from "../utils/mostrarAvisoVitalCasa";

const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function jobKey(captacionId, tipo, localId) {
  return `${captacionId}:${tipo}:${localId}`;
}

/**
 * Cola global de subidas de captación. Sigue activa aunque el usuario salga de la ficha.
 */
class CaptacionUploadQueue {
  constructor() {
    /** @type {Map<string, object>} */
    this.jobs = new Map();
    /** @type {Map<string, object>} */
    this.finalizarPendiente = new Map();
    /** @type {Map<string, object>} */
    this.uiByCaptacion = new Map();
    /** @type {Set<string>} */
    this.inflight = new Set();
    this.listeners = new Set();
    this.timer = null;
  }

  start() {
    if (this.timer) return;
    this.timer = setInterval(() => this.pump(), 800);
    this.pump();
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  notify() {
    for (const fn of this.listeners) fn(this.getGlobalStats());
  }

  attachUI(captacionId, handlers) {
    this.uiByCaptacion.set(captacionId, handlers || {});
    this.notify();
  }

  detachUI(captacionId) {
    this.uiByCaptacion.delete(captacionId);
    this.notify();
  }

  getStats(captacionId) {
    let pendientes = 0;
    let subiendo = 0;
    let errores = 0;
    let completados = 0;
    const mensajesError = [];
    for (const job of this.jobs.values()) {
      if (job.captacionId !== captacionId) continue;
      if (job.status === "subiendo") subiendo += 1;
      else if (job.status === "error") {
        errores += 1;
        if (job.error) mensajesError.push(String(job.error));
      } else if (job.status === "ok") completados += 1;
      else pendientes += 1;
    }
    return { pendientes, subiendo, errores, completados, mensajesError };
  }

  getGlobalStats() {
    let activos = 0;
    let captaciones = new Set();
    for (const job of this.jobs.values()) {
      if (job.status === "pendiente" || job.status === "subiendo") {
        activos += 1;
        captaciones.add(job.captacionId);
      }
    }
    return { activos, captaciones: [...captaciones] };
  }

  upsertFoto(captacionId, item) {
    if (!item?.file || item.subidaEstado === "ok") return;
    const key = jobKey(captacionId, "foto", item.id);
    const prev = this.jobs.get(key);
    if (prev?.status === "ok" || prev?.status === "subiendo") return;
    this.jobs.set(key, {
      captacionId,
      tipo: "foto",
      localId: item.id,
      file: item.file,
      meta: {
        tipo: item.tipo,
        descripcion_es: item.descripcion_es,
        orden: item.orden,
      },
      status: prev?.status === "error" ? "error" : "pendiente",
      error: prev?.error,
      serverId: prev?.serverId,
    });
    this.start();
    this.notify();
  }

  upsertDocumento(captacionId, item) {
    if (!item?.file || item.subidaEstado === "ok") return;
    const key = jobKey(captacionId, "doc", item.id);
    const prev = this.jobs.get(key);
    if (prev?.status === "ok" || prev?.status === "subiendo") return;
    this.jobs.set(key, {
      captacionId,
      tipo: "doc",
      localId: item.id,
      file: item.file,
      meta: {
        nombre_documento: item.nombre_documento,
        tipo_codigo: item.tipo_codigo,
        descripcion_tipo: item.descripcion_tipo,
      },
      status: prev?.status === "error" ? "error" : "pendiente",
      error: prev?.error,
      serverId: prev?.serverId,
    });
    this.start();
    this.notify();
  }

  upsertVideo(captacionId, item) {
    if (!item?.file || item.subidaEstado === "ok") return;
    const key = jobKey(captacionId, "vid", item.id);
    const prev = this.jobs.get(key);
    if (prev?.status === "ok" || prev?.status === "subiendo") return;
    this.jobs.set(key, {
      captacionId,
      tipo: "vid",
      localId: item.id,
      file: item.file,
      meta: { titulo: item.titulo },
      status: prev?.status === "error" ? "error" : "pendiente",
      error: prev?.error,
      serverId: prev?.serverId,
    });
    this.start();
    this.notify();
  }

  removeJob(captacionId, tipo, localId) {
    const key = jobKey(captacionId, tipo, localId);
    const job = this.jobs.get(key);
    if (!job || job.status === "subiendo") return;
    this.jobs.delete(key);
    this.notify();
  }

  pruneStaleJobs(captacionId, { fotoIds, docIds, vidIds }) {
    for (const [key, job] of this.jobs.entries()) {
      if (job.captacionId !== captacionId) continue;
      const still =
        (job.tipo === "foto" && fotoIds.has(job.localId)) ||
        (job.tipo === "doc" && docIds.has(job.localId)) ||
        (job.tipo === "vid" && vidIds.has(job.localId));
      if (!still && job.status !== "subiendo") {
        this.jobs.delete(key);
      }
    }
    this.notify();
  }

  reintentarErrores(captacionId) {
    for (const [key, job] of this.jobs.entries()) {
      if (job.captacionId === captacionId && job.status === "error") {
        this.jobs.set(key, { ...job, status: "pendiente", error: undefined });
      }
    }
    this.pump();
    this.notify();
  }

  /**
   * El usuario pulsó Finalizar pero aún hay archivos en cola.
   * Cuando terminen, se intentará pasar a Para Revisar.
   */
  marcarFinalizarPendiente(captacionId, syncMeta) {
    this.finalizarPendiente.set(captacionId, {
      ...syncMeta,
      avanzarAlCompletar: true,
      localToServer: syncMeta.localToServer || new Map(),
    });
    this.start();
    this.notify();
  }

  async pump() {
    const pendientes = [...this.jobs.values()].filter(
      (j) => j.status === "pendiente" && !this.inflight.has(jobKey(j.captacionId, j.tipo, j.localId))
    );
    const huecos = Math.max(0, CAPTACION_UPLOAD_PARALLEL - this.inflight.size);
    for (let i = 0; i < huecos && i < pendientes.length; i += 1) {
      this.runJob(pendientes[i]);
    }
  }

  async runJob(job, intento = 0) {
    const key = jobKey(job.captacionId, job.tipo, job.localId);
    if (this.inflight.has(key)) return;
    this.inflight.add(key);
    const current = this.jobs.get(key);
    if (!current) {
      this.inflight.delete(key);
      return;
    }
    this.jobs.set(key, { ...current, status: "subiendo", error: undefined });
    this.notifyUI(job.captacionId, job.tipo, job.localId, { subidaEstado: "subiendo" });

    try {
      let server = null;
      if (job.tipo === "foto") {
        server = await uploadCaptacionFoto(job.captacionId, job.file);
      } else if (job.tipo === "doc") {
        server = await uploadCaptacionDocumento(job.captacionId, job.file, job.meta);
      } else if (job.tipo === "vid") {
        server = await uploadCaptacionVideo(job.captacionId, job.file);
      }
      if (!server?.id) throw new Error("El servidor no devolvió el archivo");

      const rev = server.encargoRevision || null;
      // Conservamos el File si el encargo no validó: secretaría puede autorizar sin reelegir.
      const keepFile = !!(rev && rev.ok === false && job.file);
      this.jobs.set(key, {
        ...job,
        status: "ok",
        serverId: server.id,
        server,
        file: keepFile ? job.file : undefined,
        encargoRevision: rev || undefined,
      });
      this.notifyUI(job.captacionId, job.tipo, job.localId, {
        subidaEstado: "ok",
        server,
        encargoRevision: rev || undefined,
        keepFileForForzar: keepFile,
      });

      const fin = this.finalizarPendiente.get(job.captacionId);
      if (fin && job.tipo === "foto") {
        fin.localToServer.set(job.localId, server.id);
      }
    } catch (err) {
      const revErr = err.encargoRevision || err.response?.data?.encargoRevision || null;
      const msg = humanizarErrorSubida(
        revErr?.mensaje
        || err.response?.data?.error
        || err.message
        || "Error de subida",
      );
      const noRetry = !!(err.noRetry || revErr?.ok === false);
      if (!noRetry && intento < CAPTACION_UPLOAD_MAX_RETRIES - 1) {
        this.inflight.delete(key);
        await sleep(CAPTACION_UPLOAD_RETRY_MS[intento] || 5000);
        return this.runJob(job, intento + 1);
      }
      this.jobs.set(key, {
        ...job,
        status: "error",
        error: msg,
        encargoRevision: revErr || undefined,
      });
      this.notifyUI(job.captacionId, job.tipo, job.localId, {
        subidaEstado: "error",
        subidaError: msg,
        encargoRevision: revErr || undefined,
      });
      // Documento (ficha/encargo/etc.): aviso a pantalla completa, no solo el toast.
      if (job.tipo === "doc") {
        try {
          mostrarAvisoVitalCasa(msg, {
            titulo: "No se pudo subir el documento",
            boton: "Entendido",
            cerrarSoloConBoton: true,
          });
        } catch {
          /* ignore */
        }
      }
    } finally {
      this.inflight.delete(key);
      this.notify();
      await this.checkCaptacionCompleta(job.captacionId);
      this.pump();
    }
  }

  notifyUI(captacionId, tipo, localId, patch) {
    const ui = this.uiByCaptacion.get(captacionId);
    ui?.onItemUpdate?.(tipo, localId, patch);
  }

  async checkCaptacionCompleta(captacionId) {
    const stats = this.getStats(captacionId);
    if (stats.pendientes > 0 || stats.subiendo > 0) return;

    const fin = this.finalizarPendiente.get(captacionId);
    if (!fin?.avanzarAlCompletar) return;

    if (stats.errores > 0) {
      await api
        .patch(`/captaciones/${captacionId}/datos-extras`, {
          datos: {
            subida_multimedia_pendiente: true,
            ficha_finalizada_pendiente_multimedia: true,
          },
          solo_borrador: true,
        })
        .catch(() => {});
      return;
    }

    try {
      await this.aplicarSyncMeta(captacionId, fin);
      const res = await api.post(`/captaciones/${captacionId}/avanzar-si-multimedia-lista`);
      if (res.data?.avanzado) {
        this.finalizarPendiente.delete(captacionId);
        for (const [key, job] of this.jobs.entries()) {
          if (job.captacionId === captacionId) this.jobs.delete(key);
        }
        try {
          sessionStorage.setItem(
            "vitalcasa_msg",
            "Subida completada. La captación ha pasado a Para Revisar."
          );
        } catch {
          /* ignore */
        }
        this.notify();
      }
    } catch {
      /* reintentará en la siguiente subida completada */
    }
  }

  async aplicarSyncMeta(captacionId, fin) {
    if (fin.idsReportajeInicial?.length) {
      for (const fid of fin.idsReportajeInicial) {
        if (!fin.actualesReportajeIds?.includes(fid)) {
          try {
            await api.delete(`/captaciones/fotos-reportaje/${fid}`);
          } catch {
            /* ignore */
          }
        }
      }
    }

    if (fin.ordenDeseado?.length) {
      const orden = fin.ordenDeseado
        .map((id) => fin.localToServer.get(id) || id)
        .filter((id) => typeof id === "string" && UUID_RX.test(id));
      if (orden.length > 0) {
        try {
          await api.put(`/captaciones/${captacionId}/fotos-reportaje/orden`, { orden });
        } catch {
          /* ignore */
        }
      }
    }

    for (const docId of fin.documentosEliminar || []) {
      try {
        await api.delete(`/captaciones/documentos/${docId}`);
      } catch {
        /* ignore */
      }
    }

    await Promise.all(
      (fin.existentesDoc || []).map((d) =>
        api
          .patch(`/captaciones/documentos/${d.id}`, {
            tipo_codigo: d.tipo_codigo,
            descripcion_tipo: d.descripcion_tipo || null,
            nombre_documento: d.nombre_documento,
          })
          .catch(() => {})
      )
    );
  }

  buildSyncMeta({ fotos, documentos, documentosEliminar, idsReportajeInicial }) {
    const actualesReportajeIds = fotos
      .filter((f) => f.existente && f.id && UUID_RX.test(String(f.id)))
      .map((f) => f.id);
    const existentesDoc = documentos.filter((d) => d.existente && d.id);
    const localToServer = new Map();
    for (const f of fotos) {
      if (f.existente && f.id && UUID_RX.test(String(f.id))) {
        localToServer.set(f.id, f.id);
      }
    }
    return {
      ordenDeseado: fotos.map((f) => f.id),
      localToServer,
      idsReportajeInicial: idsReportajeInicial ? [...idsReportajeInicial] : [],
      actualesReportajeIds,
      documentosEliminar: [...(documentosEliminar || [])],
      existentesDoc,
    };
  }
}

export const captacionUploadQueue = new CaptacionUploadQueue();

export function initCaptacionUploadQueue() {
  captacionUploadQueue.start();
}
